import { useMemo, useRef, useState } from "react";
import { addDays, fmtShort, colorFor, taskStatus, daysSinceStart } from "@/lib/corpsColors";

const ZOOMS = {
  jour: { pxPerDay: 42, label: "Jours" },
  semaine: { pxPerDay: 14, label: "Semaines" },
  mois: { pxPerDay: 5, label: "Mois" },
};

export default function GanttChart({
  tasks,
  startDate,
  totalDays,
  zoom = "semaine",
  showCritical = true,
  editable = false,
  onTaskChange,
  onLinkCreate,
}) {
  const cfg = ZOOMS[zoom] || ZOOMS.semaine;
  const width = Math.max(600, totalDays * cfg.pxPerDay + 40);
  const rowH = 40;
  const height = tasks.length * rowH + 60;
  const [hover, setHover] = useState(null);
  const [drag, setDrag] = useState(null); // {id, mode, startX, initStart, initDur}
  const svgRef = useRef(null);

  const scale = useMemo(() => {
    const marks = [];
    let step = 1;
    if (zoom === "semaine") step = 7;
    if (zoom === "mois") step = 14;
    for (let d = 0; d <= totalDays; d += step) {
      marks.push({ d, x: d * cfg.pxPerDay });
    }
    return marks;
  }, [totalDays, cfg.pxPerDay, zoom]);

  const taskById = useMemo(() => {
    const m = {};
    tasks.forEach((t) => (m[t.id] = t));
    return m;
  }, [tasks]);

  const startDrag = (e, task, mode) => {
    if (!editable) return;
    e.stopPropagation();
    e.preventDefault();
    setDrag({
      id: task.id,
      mode,
      startX: e.clientX,
      initStart: task.start_day,
      initDur: task.duration_days,
    });
  };

  const onMouseMove = (e) => {
    if (!drag) return;
    const dxDays = Math.round((e.clientX - drag.startX) / cfg.pxPerDay);
    if (drag.mode === "move") {
      const newStart = Math.max(0, drag.initStart + dxDays);
      onTaskChange?.(drag.id, { start_day: newStart, duration_days: drag.initDur });
    } else if (drag.mode === "resize") {
      const newDur = Math.max(1, drag.initDur + dxDays);
      onTaskChange?.(drag.id, { start_day: drag.initStart, duration_days: newDur });
    }
  };

  const endDrag = () => setDrag(null);

  return (
    <div
      className="overflow-x-auto gantt-scroll bg-white rounded-lg border border-slate-200"
      data-testid="gantt-chart"
    >
      <svg
        ref={svgRef}
        width={width}
        height={height}
        className="block select-none"
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
      >
        {scale.map((m, i) => (
          <g key={i}>
            <line
              x1={m.x}
              x2={m.x}
              y1={30}
              y2={height}
              stroke="rgba(15,33,55,0.08)"
              strokeDasharray={i % 2 ? "2 4" : ""}
            />
            <text
              x={m.x + 4}
              y={20}
              fontSize="10"
              fill="#64748B"
              fontFamily="JetBrains Mono, monospace"
            >
              {fmtShort(addDays(startDate, m.d))}
            </text>
          </g>
        ))}

        {tasks.map((t, i) =>
          (t.dependencies || []).map((depId) => {
            const dep = taskById[depId];
            if (!dep) return null;
            const depIdx = tasks.findIndex((x) => x.id === depId);
            if (depIdx < 0) return null;
            const x1 = (dep.start_day + dep.duration_days) * cfg.pxPerDay;
            const y1 = depIdx * rowH + 30 + rowH / 2;
            const x2 = t.start_day * cfg.pxPerDay;
            const y2 = i * rowH + 30 + rowH / 2;
            return (
              <path
                key={`${t.id}-${depId}`}
                d={`M ${x1} ${y1} L ${x1 + 6} ${y1} L ${x1 + 6} ${y2} L ${x2} ${y2}`}
                stroke="#94A3B8"
                strokeWidth="1.2"
                fill="none"
                markerEnd="url(#arrow)"
              />
            );
          })
        )}
        <defs>
          <marker id="arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
            <path d="M0,0 L7,4 L0,8 Z" fill="#94A3B8" />
          </marker>
          <pattern id="hatchLate" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" fill="transparent" />
            <line x1="0" y1="0" x2="0" y2="6" stroke="#DC2626" strokeWidth="2" opacity="0.5" />
          </pattern>
        </defs>

        {tasks.map((t, i) => {
          const x = t.start_day * cfg.pxPerDay;
          const w = Math.max(6, t.duration_days * cfg.pxPerDay);
          const y = i * rowH + 30 + 6;
          const bh = rowH - 12;
          const color = colorFor(t.corps_etat);
          const isCritical = showCritical && t.is_critical;
          const isDragging = drag?.id === t.id;
          const status = taskStatus(t, startDate);
          const isLate = status === "late";
          return (
            <g
              key={t.id}
              onMouseEnter={() => setHover(t.id)}
              onMouseLeave={() => setHover(null)}
              data-testid={`gantt-bar-${t.id}`}
            >
              <rect
                x={x}
                y={y}
                width={w}
                height={bh}
                rx={4}
                fill={color}
                opacity={hover && hover !== t.id ? 0.55 : 0.95}
                stroke={isLate ? "#DC2626" : isCritical ? "#0F2137" : "none"}
                strokeWidth={isLate ? 2.5 : isCritical ? 2 : 0}
                onMouseDown={(e) => {
                  if (editable && (e.altKey || e.shiftKey)) {
                    e.stopPropagation();
                    e.preventDefault();
                    setDrag({ id: t.id, mode: "link", startX: e.clientX, startY: e.clientY });
                  } else {
                    startDrag(e, t, "move");
                  }
                }}
                onMouseUp={(e) => {
                  if (drag?.mode === "link" && drag.id !== t.id) {
                    onLinkCreate?.(drag.id, t.id);
                    setDrag(null);
                  }
                }}
                style={{ cursor: editable ? (isDragging ? "grabbing" : "grab") : "pointer" }}
              />
              {isLate && (
                <rect
                  x={x}
                  y={y}
                  width={w}
                  height={bh}
                  rx={4}
                  fill="url(#hatchLate)"
                  pointerEvents="none"
                />
              )}
              {w > 60 ? (
                <text
                  x={x + 8}
                  y={y + bh / 2 + 4}
                  fontSize="11"
                  fill="#fff"
                  fontWeight="600"
                  fontFamily="Inter, sans-serif"
                  pointerEvents="none"
                >
                  {t.name.length > w / 7
                    ? t.name.slice(0, Math.max(6, Math.floor(w / 7))) + "…"
                    : t.name}
                </text>
              ) : (
                <text
                  x={x + w + 4}
                  y={y + bh / 2 + 4}
                  fontSize="11"
                  fill="#0F2137"
                  fontWeight="500"
                  fontFamily="Inter, sans-serif"
                  pointerEvents="none"
                >
                  {t.name.length > 40 ? t.name.slice(0, 40) + "…" : t.name}
                </text>
              )}
              {editable && (
                <rect
                  x={x + w - 6}
                  y={y}
                  width={8}
                  height={bh}
                  fill="rgba(255,255,255,0.35)"
                  stroke="#0F2137"
                  strokeWidth="1"
                  rx={2}
                  style={{ cursor: "ew-resize" }}
                  onMouseDown={(e) => startDrag(e, t, "resize")}
                  data-testid={`gantt-resize-${t.id}`}
                />
              )}
            </g>
          );
        })}

        {/* Today marker */}
        {(() => {
          const today = daysSinceStart(startDate);
          if (today < 0 || today > totalDays) return null;
          const tx = today * cfg.pxPerDay;
          return (
            <g data-testid="today-marker">
              <line x1={tx} x2={tx} y1={25} y2={height} stroke="#DC2626" strokeWidth="1.5" strokeDasharray="4 3" />
              <rect x={tx - 22} y={5} width={44} height={16} rx={3} fill="#DC2626" />
              <text x={tx} y={16} fontSize="9" fill="#fff" textAnchor="middle" fontWeight="700" fontFamily="Inter, sans-serif">
                AUJ.
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}
